import React, { useState, useEffect, useCallback } from 'react';
import { Container, Alert, Spinner, Badge, Form } from 'react-bootstrap';
import {
  collection, getDocs, orderBy, query,
  updateDoc, doc
} from 'firebase/firestore';
import { database } from '../Firebase';

const CATEGORIES = [
  { key: 'tous',       label: '📋 Toutes',      color: '#6c757d' },
  { key: 'bug',        label: '🐛 Bugs',         color: '#dc3545' },
  { key: 'suggestion', label: '💡 Suggestions',  color: '#0d6efd' },
  { key: 'compliment', label: '❤️ Compliments',  color: '#198754' },
  { key: 'autre',      label: '💬 Autres',       color: '#6c757d' },
];

const STATUTS = [
  { key: 'tous',    label: 'Tous les statuts' },
  { key: 'nouveau', label: '🟡 Nouveau'       },
  { key: 'lu',      label: '🔵 Lu'            },
  { key: 'traité',  label: '🟢 Traité'        },
];

export default function AdminSuggestions() {
  const [suggestions, setSuggestions]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('tous');
  const [filtreStatut, setFiltreStatut]     = useState('tous');
  const [filtrePionnier, setFiltrePionnier] = useState(false);
  const [reponseEnCours, setReponseEnCours] = useState(null); // id suggestion
  const [reponseTexte, setReponseTexte]     = useState('');
  const [savingReponse, setSavingReponse]   = useState(false);

  const chargerSuggestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const snap = await getDocs(query(
        collection(database, 'suggestions'),
        orderBy('dateCreation', 'desc')
      ));
      setSuggestions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      setError("Erreur lors du chargement : " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { chargerSuggestions(); }, [chargerSuggestions]);

  // ── Filtre les suggestions ────────────────────────────────────────────────
  const suggestionsFiltrees = suggestions.filter(s => {
    if (filtreCategorie !== 'tous' && s.categorie !== filtreCategorie) return false;
    if (filtreStatut    !== 'tous' && s.statut    !== filtreStatut)    return false;
    if (filtrePionnier  && !s.estPionnier)                             return false;
    return true;
  });

  // ── Changer le statut d'une suggestion ────────────────────────────────────
  const changerStatut = async (id, nouveauStatut) => {
    try {
      await updateDoc(doc(database, 'suggestions', id), { statut: nouveauStatut });
      setSuggestions(prev => prev.map(s =>
        s.id === id ? { ...s, statut: nouveauStatut } : s
      ));
    } catch (err) {
      setError("Erreur lors de la mise à jour du statut.");
    }
  };

  // ── Envoyer une réponse admin ─────────────────────────────────────────────
  const envoyerReponse = async (id) => {
    if (!reponseTexte.trim()) return;
    setSavingReponse(true);
    try {
      await updateDoc(doc(database, 'suggestions', id), {
        reponseAdmin: reponseTexte.trim(),
        statut:       'traité',
        dateReponse:  new Date().toISOString(),
      });
      setSuggestions(prev => prev.map(s =>
        s.id === id
          ? { ...s, reponseAdmin: reponseTexte.trim(), statut: 'traité' }
          : s
      ));
      setReponseEnCours(null);
      setReponseTexte('');
    } catch (err) {
      setError("Erreur lors de l'envoi de la réponse.");
    } finally {
      setSavingReponse(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = d?.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getCatConfig = (key) =>
    CATEGORIES.find(c => c.key === key) || CATEGORIES[0];

  const getStatutConfig = (statut) => {
    const config = {
      nouveau: { label: 'Nouveau',   bg: '#ffc107', color: '#000' },
      lu:      { label: 'Lu ✓',      bg: '#0d6efd', color: '#fff' },
      traité:  { label: 'Traité ✅',  bg: '#198754', color: '#fff' },
    };
    return config[statut] || config.nouveau;
  };

  const noteEtoiles = (note) => {
    if (!note) return '—';
    return '★'.repeat(note) + '☆'.repeat(5 - note);
  };

  // ── Stats rapides ─────────────────────────────────────────────────────────
  const statsRapides = {
    total:      suggestions.length,
    nouveaux:   suggestions.filter(s => s.statut === 'nouveau').length,
    bugs:       suggestions.filter(s => s.categorie === 'bug').length,
    notesMoy:   suggestions.length > 0
      ? (suggestions.reduce((sum, s) => sum + (s.note || 0), 0) / suggestions.length).toFixed(1)
      : 0,
    pionniers:  suggestions.filter(s => s.estPionnier).length,
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: '#07a993' }} />
        <p className="mt-2 text-muted">Chargement des suggestions...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4" style={{ maxWidth: '1000px' }}>

      {/* ── En-tête ──────────────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 style={{ fontWeight: 700, marginBottom: '2px' }}>
            💡 Suggestions utilisateurs
          </h4>
          <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>
            {statsRapides.total} suggestion{statsRapides.total > 1 ? 's' : ''} reçue{statsRapides.total > 1 ? 's' : ''}
          </span>
        </div>
        <button onClick={chargerSuggestions} style={{
          padding: '6px 14px', borderRadius: '8px',
          border: '2px solid #dee2e6', background: '#fff',
          fontSize: '0.85rem', cursor: 'pointer',
        }}>
          🔄 Actualiser
        </button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* ── Stats rapides ─────────────────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        {[
          { label: 'Total',          value: statsRapides.total,    color: '#07a993', bg: '#e8f8f5', icon: '💬' },
          { label: 'Non lus',        value: statsRapides.nouveaux, color: '#ffc107', bg: '#fff3cd', icon: '🔔' },
          { label: 'Bugs signalés',  value: statsRapides.bugs,     color: '#dc3545', bg: '#fce8e8', icon: '🐛' },
          { label: 'Note moyenne',   value: `${statsRapides.notesMoy}/5`, color: '#fd7e14', bg: '#fff3e0', icon: '⭐' },
          { label: 'De pionniers',   value: statsRapides.pionniers, color: '#0d6efd', bg: '#e8f0fe', icon: '🏅' },
        ].map(({ label, value, color, bg, icon }) => (
          <div key={label} className="col-6 col-lg">
            <div className="p-3 rounded text-center" style={{
              background: bg, border: `1px solid ${color}33`
            }}>
              <div style={{ fontSize: '1.3rem' }}>{icon}</div>
              <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '4px' }}>
                {label}
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color }}>
                {value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filtres ───────────────────────────────────────────────────────────── */}
      <div className="p-3 rounded mb-4" style={{
        background: '#f8f9fa', border: '1px solid #dee2e6'
      }}>
        <div className="row g-2 align-items-center">

          {/* Filtre catégorie */}
          <div className="col-12 col-md-4">
            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '6px', fontWeight: 600 }}>
              Catégorie
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {CATEGORIES.map(cat => (
                <button key={cat.key}
                  onClick={() => setFiltreCategorie(cat.key)}
                  style={{
                    padding: '4px 10px', borderRadius: '16px', cursor: 'pointer',
                    border: `1px solid ${filtreCategorie === cat.key ? cat.color : '#dee2e6'}`,
                    background: filtreCategorie === cat.key ? cat.color : '#fff',
                    color: filtreCategorie === cat.key ? '#fff' : '#333',
                    fontSize: '0.78rem', fontWeight: 600,
                    transition: 'all 0.15s ease',
                  }}>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filtre statut */}
          <div className="col-12 col-md-4">
            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '6px', fontWeight: 600 }}>
              Statut
            </div>
            <Form.Select
              value={filtreStatut}
              onChange={e => setFiltreStatut(e.target.value)}
              style={{ fontSize: '0.85rem' }}
            >
              {STATUTS.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </Form.Select>
          </div>

          {/* Filtre pionniers */}
          <div className="col-12 col-md-4">
            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '6px', fontWeight: 600 }}>
              Origine
            </div>
            <Form.Check
              type="switch"
              id="filtre-pionnier"
              label="Pionniers uniquement"
              checked={filtrePionnier}
              onChange={e => setFiltrePionnier(e.target.checked)}
            />
          </div>
        </div>
      </div>

      {/* ── Résultats ─────────────────────────────────────────────────────────── */}
      <div style={{ fontSize: '0.82rem', color: '#6c757d', marginBottom: '12px' }}>
        {suggestionsFiltrees.length} résultat{suggestionsFiltrees.length > 1 ? 's' : ''}
      </div>

      {suggestionsFiltrees.length === 0 ? (
        <div className="text-center py-5" style={{
          border: '2px dashed #dee2e6', borderRadius: '12px', background: '#f8f9fa'
        }}>
          <div style={{ fontSize: '2.5rem' }}>💡</div>
          <p className="mt-2 text-muted">Aucune suggestion ne correspond aux filtres.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {suggestionsFiltrees.map(s => {
            const catConfig    = getCatConfig(s.categorie);
            const statutConfig = getStatutConfig(s.statut);
            const isReplying   = reponseEnCours === s.id;

            return (
              <div key={s.id} className="p-3 rounded" style={{
                background: '#fff',
                border: `1px solid ${catConfig.color}33`,
                borderLeft: `4px solid ${catConfig.color}`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
              }}>

                {/* ── En-tête suggestion ──────────────────────────────────── */}
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Catégorie */}
                    <span style={{
                      fontSize: '0.8rem', fontWeight: 700,
                      color: catConfig.color
                    }}>
                      {catConfig.label}
                    </span>
                    {/* Statut */}
                    <span style={{
                      fontSize: '0.7rem', background: statutConfig.bg,
                      color: statutConfig.color, padding: '2px 8px',
                      borderRadius: '10px', fontWeight: 600
                    }}>
                      {statutConfig.label}
                    </span>
                    {/* Badge pionnier */}
                    {s.estPionnier && (
                      <span style={{
                        fontSize: '0.7rem',
                        background: 'linear-gradient(135deg, #07a993, #0d6efd)',
                        color: '#fff', padding: '2px 8px',
                        borderRadius: '10px', fontWeight: 600
                      }}>
                        🏅 Pionnier #{s.numeroPionnier}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                    {formatDate(s.dateCreation)}
                  </span>
                </div>

                {/* ── Infos utilisateur ────────────────────────────────────── */}
                <div style={{
                  fontSize: '0.82rem', color: '#6c757d', marginBottom: '8px'
                }}>
                  👤 <strong>{s.pseudo}</strong>
                  {s.secteur && ` — ${s.secteur}`}
                  <span style={{ marginLeft: '12px', color: '#ffc107' }}>
                    {noteEtoiles(s.note)}
                    <span style={{ color: '#6c757d', marginLeft: '4px' }}>
                      ({s.note}/5)
                    </span>
                  </span>
                </div>

                {/* ── Contenu ─────────────────────────────────────────────── */}
                <p style={{
                  fontSize: '0.92rem', color: '#333',
                  marginBottom: '12px', lineHeight: 1.5
                }}>
                  {s.description}
                </p>

                {/* ── Réponse admin existante ──────────────────────────────── */}
                {s.reponseAdmin && !isReplying && (
                  <div className="p-2 rounded mb-2" style={{
                    background: '#e8f8f5', border: '1px solid #07a993',
                    fontSize: '0.85rem'
                  }}>
                    <strong style={{ color: '#07a993' }}>
                      💬 Votre réponse :
                    </strong>
                    <p className="mb-0 mt-1">{s.reponseAdmin}</p>
                  </div>
                )}

                {/* ── Zone de réponse ─────────────────────────────────────── */}
                {isReplying && (
                  <div className="mb-3">
                    <Form.Control
                      as="textarea" rows={3}
                      value={reponseTexte}
                      onChange={e => setReponseTexte(e.target.value)}
                      placeholder="Votre réponse à l'utilisateur..."
                      style={{ fontSize: '0.88rem', marginBottom: '8px' }}
                      autoFocus
                    />
                    <div className="d-flex gap-2">
                      <button
                        onClick={() => envoyerReponse(s.id)}
                        disabled={savingReponse || !reponseTexte.trim()}
                        style={{
                          padding: '6px 16px', borderRadius: '8px',
                          background: '#07a993', color: '#fff',
                          border: 'none', fontWeight: 600,
                          fontSize: '0.85rem', cursor: 'pointer',
                        }}>
                        {savingReponse ? 'Envoi...' : '✅ Envoyer la réponse'}
                      </button>
                      <button
                        onClick={() => {
                          setReponseEnCours(null);
                          setReponseTexte('');
                        }}
                        style={{
                          padding: '6px 16px', borderRadius: '8px',
                          background: '#f8f9fa', color: '#333',
                          border: '1px solid #dee2e6',
                          fontSize: '0.85rem', cursor: 'pointer',
                        }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Actions admin ────────────────────────────────────────── */}
                <div className="d-flex gap-2 flex-wrap">

                  {/* Changer statut */}
                  {s.statut === 'nouveau' && (
                    <button
                      onClick={() => changerStatut(s.id, 'lu')}
                      style={{
                        padding: '4px 12px', borderRadius: '6px',
                        border: '1px solid #0d6efd', background: '#fff',
                        color: '#0d6efd', fontSize: '0.78rem',
                        cursor: 'pointer', fontWeight: 600,
                      }}>
                      Marquer comme lu
                    </button>
                  )}

                  {s.statut === 'lu' && (
                    <button
                      onClick={() => changerStatut(s.id, 'traité')}
                      style={{
                        padding: '4px 12px', borderRadius: '6px',
                        border: '1px solid #198754', background: '#fff',
                        color: '#198754', fontSize: '0.78rem',
                        cursor: 'pointer', fontWeight: 600,
                      }}>
                      Marquer comme traité
                    </button>
                  )}

                  {/* Répondre */}
                  {!isReplying && (
                    <button
                      onClick={() => {
                        setReponseEnCours(s.id);
                        setReponseTexte(s.reponseAdmin || '');
                        if (s.statut === 'nouveau') changerStatut(s.id, 'lu');
                      }}
                      style={{
                        padding: '4px 12px', borderRadius: '6px',
                        border: '1px solid #07a993', background: '#fff',
                        color: '#07a993', fontSize: '0.78rem',
                        cursor: 'pointer', fontWeight: 600,
                      }}>
                      {s.reponseAdmin ? '✏️ Modifier la réponse' : '💬 Répondre'}
                    </button>
                  )}

                  {/* Remettre en nouveau si besoin */}
                  {s.statut === 'traité' && (
                    <button
                      onClick={() => changerStatut(s.id, 'nouveau')}
                      style={{
                        padding: '4px 12px', borderRadius: '6px',
                        border: '1px solid #6c757d', background: '#fff',
                        color: '#6c757d', fontSize: '0.78rem',
                        cursor: 'pointer',
                      }}>
                      Rouvrir
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Container>
  );
}